/**
 * What a flagged area's entry row shows about BC on-hand — **Cold Link only**
 * (Copeland 2026-10-29, `docs/phases/PHASE_coldlink-onhand-entry.md`, amending
 * D-1). Everything on-hand-shaped on the entry screen goes through this file, so
 * "where can entry see on-hand" has exactly one answer.
 *
 * **Display arithmetic, never sent anywhere.** The difference needs the figure a
 * manager is typing right now, which the server cannot have yet, so it is worked
 * out here — in exact decimal (BigInt-scaled), never floating point, and with the
 * **same factor the review screen converts with**: `unit_options[].qty_per_base`
 * from the count list, BC's own `Qty. per Unit of Measure`. No second conversion
 * exists. What syncs to staging is still only the typed string and a unit code.
 *
 * **Blank stays blank** (rule 5): no typed quantity, no difference, no warning.
 * **Never prefilled**: nothing here produces a value for the entry box.
 */
import type { CountListItem, DecimalString } from "../api/types";

interface Dec {
  n: bigint;
  scale: number;
}

function parse(text: string): Dec | null {
  const m = /^(-?)(\d*)\.?(\d*)$/.exec(text.trim());
  if (!m || (m[2] === "" && m[3] === "")) return null;
  const whole = m[2] ?? "";
  const frac = m[3] ?? "";
  const n = BigInt((whole || "0") + frac) * (m[1] ? -1n : 1n);
  return { n, scale: frac.length };
}

function align(a: Dec, b: Dec): [bigint, bigint, number] {
  const scale = Math.max(a.scale, b.scale);
  return [a.n * 10n ** BigInt(scale - a.scale), b.n * 10n ** BigInt(scale - b.scale), scale];
}

function format(n: bigint, scale: number): string {
  const neg = n < 0n;
  const digits = (neg ? -n : n).toString().padStart(scale + 1, "0");
  let out = scale === 0 ? digits : `${digits.slice(0, -scale)}.${digits.slice(-scale)}`;
  if (out.includes(".")) out = out.replace(/0+$/, "").replace(/\.$/, "");
  return neg && out !== "0" ? `-${out}` : out;
}

const DIVIDE_PLACES = 4;

/** `a ÷ b` to four decimal places; `exact` is false when rounding was needed. */
function divide(a: Dec, b: Dec): { text: string; exact: boolean } | null {
  const [x, y] = align(a, b);
  if (y === 0n) return null;
  const scaled = x * 10n ** BigInt(DIVIDE_PLACES);
  return { text: format(scaled / y, DIVIDE_PLACES), exact: scaled % y === 0n };
}

export interface OnHandRowView {
  /** "BC on hand: 120 CS" — base, or entry unit with base in parentheses. */
  onHandLabel: string;
  /** "−5 CS vs BC" once a quantity is typed and a difference is computable. */
  differenceLabel: string | null;
  /** Counted strictly below BC: a warning, never a block. */
  belowBc: boolean;
}

export function onHandRowView(
  item: CountListItem,
  onHandBaseQty: DecimalString,
  base: string | null,
  entryUnit: string | null,
  typedQty: string | null | undefined,
): OnHandRowView | null {
  const onHand = parse(onHandBaseQty);
  if (!onHand) return null;
  const baseUnit = base ?? item.base_unit_of_measure ?? "";
  const onHandBase = format(onHand.n, onHand.scale);

  const chosen = entryUnit?.trim().toUpperCase() ?? "";
  const differs = chosen !== "" && baseUnit !== "" && chosen !== baseUnit.toUpperCase();
  const option = differs
    ? item.unit_options?.find((o) => o.code.trim().toUpperCase() === chosen)
    : undefined;
  const factor = option?.qty_per_base ? parse(option.qty_per_base) : null;

  let onHandLabel = `BC on hand: ${onHandBase} ${baseUnit}`.trim();
  if (differs && factor) {
    const inEntry = divide(onHand, factor);
    if (inEntry) {
      onHandLabel = `BC on hand: ${inEntry.exact ? "" : "≈"}${inEntry.text} ${entryUnit} (${onHandBase} ${baseUnit})`;
    }
  }

  const counted = typedQty !== null && typedQty !== undefined ? parse(typedQty) : null;
  // A unit with no usable factor gets no difference — the review screen's rule.
  if (!counted || (differs && !factor)) {
    return { onHandLabel, differenceLabel: null, belowBc: false };
  }
  const countedBase: Dec = differs && factor
    ? { n: counted.n * factor.n, scale: counted.scale + factor.scale }
    : counted;
  const [c, o, scale] = align(countedBase, onHand);
  const delta = c - o;
  const sign = delta > 0n ? "+" : "";
  return {
    onHandLabel,
    differenceLabel: `${sign}${format(delta, scale)} ${baseUnit} vs BC`.trim(),
    belowBc: delta < 0n,
  };
}
